import {OUTCOME_EXISTING, OUTCOME_RENEWED} from '../../../api/shopifyLinkRequestsApi';

// Lo que se decide sobre un pedido de link de instalación de Shopify (`contrato.md` §3, `copy.md` §1 y §2),
// sin React: qué dice cada renglón del aviso de Conexiones, qué botones lleva y qué muestra el modal cuando
// el pedido salió. Las fechas, `dd/mm` en la hora local del navegador (contrato §0).

export const STATE_PREPARING = 'preparing';
export const STATE_READY = 'ready';
export const STATE_EXPIRED = 'expired';

export const ACTION_INSTALL = 'install';
export const ACTION_COPY = 'copy';
export const ACTION_RENEW = 'renew';

/** `2026-10-30T18:10:00+00:00` → `30/10` en la hora local. Sin fecha válida, `''`. */
export function formatDayMonth(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

/**
 * El link sólo se usa si es `https://`: se pone en un `href` y en el portapapeles. El backend ya valida que
 * sea uno de `admin.shopify.com` al guardarlo (`INVALID_LINK`); esto es la segunda llave.
 */
export function installLinkOf(request) {
    const link = request && request.install_link;
    return typeof link === 'string' && /^https:\/\//i.test(link) ? link : null;
}

/**
 * Un texto con el vencimiento: `{key, args: {..., date}}`, o la variante `<key>_no_date` sin la fecha si el
 * pedido no la trae (QA-sl-fe-N03). Un link guardado sin vencimiento está `ready` con `expires_at: null`, y el
 * texto decía «Vence el .». `expired` siempre trae fecha (el backend lo calcula con ella); la variante es la red.
 */
function withExpiry(key, args, iso) {
    const date = formatDayMonth(iso);
    return date ? {key, args: {...args, date}} : {key: `${key}_no_date`, args};
}

/**
 * Un renglón del aviso: `{id, shop, clientName, state, text: {key, args}, actions}`, o `null` si el estado no
 * es uno de los tres que se muestran (un backend más nuevo con un estado que este bundle no conoce no deja un
 * renglón mudo). `ready` sin un link usable no ofrece Instalar ni Copiar.
 */
export function requestRowOf(request) {
    if (!request) return null;
    const base = {
        id: request.id,
        shop: request.shop,
        clientName: (request.agency_client && request.agency_client.name) || null,
        state: request.state,
    };
    if (request.state === STATE_PREPARING) {
        return {...base, text: {key: 'shopify_link.requests.preparing', args: {}}, actions: []};
    }
    if (request.state === STATE_READY) {
        return {
            ...base,
            text: withExpiry('shopify_link.requests.ready', {}, request.expires_at),
            actions: installLinkOf(request) ? [ACTION_INSTALL, ACTION_COPY] : [],
            installLink: installLinkOf(request),
        };
    }
    if (request.state === STATE_EXPIRED) {
        return {
            ...base,
            text: withExpiry('shopify_link.requests.expired', {}, request.expires_at),
            actions: [ACTION_RENEW],
        };
    }
    return null;
}

/** Los renglones del aviso, en el orden del backend (el más nuevo primero). */
export function requestRowsOf(requests) {
    return (Array.isArray(requests) ? requests : []).map(requestRowOf).filter(Boolean);
}

/**
 * Qué muestra el modal cuando el pedido salió (`copy.md` §1): `{titleKey, lines: [{key, args}], linkActions}`.
 *
 * - `created`: título, «te enviamos el link a…» y quién lo puede instalar.
 * - `existing` + `ready`: el link ya está, con su vencimiento, Instalar y Copiar.
 * - `existing` + otro estado: ya había un pedido, de tal fecha. (`existing` nunca llega `expired`: un pedido
 *   vencido se renueva y vuelve como `renewed`.)
 * - `renewed`: título propio y el mismo «te enviamos el link a…».
 *
 * `notifyEmail` es a quién le va a llegar el link. Si no hay (el usuario que lo pidió ya no existe), el texto
 * no lo nombra: la variante `_no_email` dice sólo que aparece en Conexiones.
 */
export function confirmationOf({outcome, request, notifyEmail}) {
    const shop = (request && request.shop) || '';
    const email = notifyEmail || null;
    const createdBody = email
        ? {key: 'shopify_link.created_body', args: {shop, email}}
        : {key: 'shopify_link.created_body_no_email', args: {shop}};

    if (outcome === OUTCOME_EXISTING) {
        if (request && request.state === STATE_READY && installLinkOf(request)) {
            return {
                titleKey: null,
                lines: [
                    withExpiry('shopify_link.existing_ready', {shop}, request.expires_at),
                    {key: 'shopify_link.who_installs', args: {}},
                ],
                linkActions: true,
            };
        }
        const date = formatDayMonth(request && request.requested_at);
        return {
            titleKey: null,
            lines: [email
                ? {key: 'shopify_link.existing_preparing', args: {shop, date, email}}
                : {key: 'shopify_link.existing_preparing_no_email', args: {shop, date}}],
            linkActions: false,
        };
    }
    if (outcome === OUTCOME_RENEWED) {
        return {titleKey: 'shopify_link.renewed_title', lines: [createdBody], linkActions: false};
    }
    // `created`, y cualquier `outcome` que este bundle no conozca: el pedido existe, que es lo que importa.
    return {
        titleKey: 'shopify_link.created_title',
        lines: [createdBody, {key: 'shopify_link.who_installs', args: {}}],
        linkActions: false,
    };
}
